import * as Cookies from "es-cookie";

// ── Exam-type (target-exam) scope helpers ───────────────────────────────────
// A GLOBAL exam-type selection (NEET / JEE Mains / JEE Advanced / EAMCET) is
// chosen in the header (NavbarOne) and REMEMBERED across pages via the
// "exam_type" cookie — mirroring the branchScope.js pattern.
//
// Backend note: exam_paper.exam_type is an INTEGER FK to the `exam` master
// table. Only NEET(1) and JEE Mains(2) are confirmed in backend source; the
// Advanced / EAMCET ids are DB rows we have NOT confirmed yet. So those two are
// left as null here: a null id means "label shown, but no exam_type is sent"
// (behaves like All) until the real ids are filled in — we never send a wrong
// id and silently mis-filter. Fill EXAM_TYPES[*].id below once confirmed.
//
// Empty / no selection ("") = show ALL exams (no filter), matching the app's
// existing mixed-results behaviour.

export const EXAM_TYPE_COOKIE = "exam_type";

// The four selectable exam types. `id` is the backend exam_paper.exam_type int.
// key   — stable cookie value
// label — header text
// id    — backend exam_type int (null = not yet confirmed → treated as All)
export const EXAM_TYPES = [
  { key: "neet", label: "NEET", id: 1 },
  { key: "jee_mains", label: "JEE Mains", id: 2 },
  { key: "jee_adv", label: "JEE Advanced", id: null }, // TODO: confirm exam id
  { key: "eamcet", label: "EAMCET", id: null }, // TODO: confirm exam id
];

// The currently-selected exam-type KEY ("" when none / All).
export function getExamTypeKey() {
  return Cookies.get(EXAM_TYPE_COOKIE) || "";
}

// The full selected exam-type object, or null for All.
export function getExamType() {
  const key = getExamTypeKey();
  if (!key) return null;
  return EXAM_TYPES.find((t) => t.key === key) || null;
}

// The backend exam_type integer to send in queries, or null for "no filter"
// (All, or a type whose id isn't confirmed yet). Callers should OMIT the
// exam_type variable when this returns null so nothing is over-filtered.
export function getExamTypeId() {
  const t = getExamType();
  return t && t.id != null ? t.id : null;
}

// Human label for the current selection (for headers / breadcrumbs). "" → "All Exams".
export function getExamTypeLabel() {
  const t = getExamType();
  return t ? t.label : "All Exams";
}

// Persist a new selection (pass "" to clear → All) and broadcast a change so
// open pages can refetch. Components can listen via onExamTypeChange().
export function setExamTypeKey(key) {
  if (key) Cookies.set(EXAM_TYPE_COOKIE, key);
  else Cookies.remove(EXAM_TYPE_COOKIE);
  try {
    window.dispatchEvent(
      new CustomEvent("examtypechange", { detail: { key: key || "" } }),
    );
  } catch (e) {
    /* CustomEvent unsupported — cookie is still set; next navigation picks it up */
  }
}

// Subscribe to global exam-type changes. Returns an unsubscribe fn.
//   const off = onExamTypeChange(() => this.reload());
//   // later: off();
export function onExamTypeChange(handler) {
  const wrapped = (e) => handler((e && e.detail && e.detail.key) || "");
  window.addEventListener("examtypechange", wrapped);
  return () => window.removeEventListener("examtypechange", wrapped);
}
